import classnames from 'classnames'
import { ComponentProps, PropsWithChildren, ReactNode } from 'react'
import { Icon, Badge } from '@atoms'

export type MenuItemProps = PropsWithChildren & {
    path: string
    label: string
    variant: 'primary' | 'destructive' | 'ghost'
    hideLabel?: boolean
    icon?: string
    iconPosition?: 'start' | 'end'
    iconSize?: ComponentProps<typeof Icon>['size']
    render?: React.FC<RenderMenuItemFnProps>
    className?: string
    labelWrapperClassname?: string
    isActive?: boolean
    disabled?: boolean
    badge?: string
}

const defaultRenderFn = (props: RenderMenuItemFnProps) => {
    const { renderContents } = props
    return <button>{renderContents()}</button>
}

export type RenderMenuItemFnProps = {
    item: MenuItemProps
    renderContents: (active?: boolean) => ReactNode
}

export const MenuItem: React.FC<MenuItemProps> = (item) => {
    const {
        label,
        variant = 'primary',
        hideLabel = false,
        icon,
        iconPosition = 'start',
        iconSize,
        render,
        labelWrapperClassname,
        badge,
    } = item

    const renderFn = render ?? defaultRenderFn

    const baseStyles = classnames(
        'flex items-center justify-between py-2 px-4 gap-2 text-label-small focus:outline-4'
    )

    const variants = {
        primary: {
            default: 'bg-white text-subtle',
            focused: 'focus:bg-white focus:text-regular focus:outline-azure/35',
            hover: 'hover:bg-blue-50 hover:text-regular',
            clicked: 'bg-neutral-200 text-black',
            selected: '[&.active]:bg-blue-50 [&.active]:text-azure',
        },
        destructive: {
            default: 'bg-white text-error',
            focused: 'focus:bg-white focus:text-red-600 focus:outline-error/25',
            hover: 'hover:bg-red-50 hover:text-red-600',
            clicked: 'active:bg-red-100 active:text-red-700',
            selected: '[&.active]:bg-red-100 [&.active]:text-error',
        },
        ghost: {
            default: 'bg-white text-neutral-900',
            focused:
                'group-focus:outline-4 group-focus:outline-azure/35 group-focus:outline',
            hover: 'hover:text-black hover:bg-blue-50',
            clicked:
                'group-active:text-black group-active:bg-blue-100 group-active:outline-none',
            selected:
                'group-active:text-black group-active:bg-blue-100 group-active:outline-none',
        },
    }

    const variantObj = variants[variant]

    const className = classnames(
        baseStyles,
        variantObj.default,
        variantObj.hover,
        variantObj.selected,
        variantObj.clicked,
        variantObj.focused,
        item.className
    )

    const labelClassname = classnames(
        'overflow-hidden whitespace-nowrap',
        {
            'pl-2': icon && iconPosition === 'start' && !hideLabel,
            'pr-2': icon && iconPosition === 'end' && !hideLabel,
        },
        labelWrapperClassname
    )

    const renderIcon = () => {
        if (!icon) {
            return null
        }

        return <Icon name={icon} size={iconSize} />
    }

    const gridClassname = classnames('grid transition-all items-center', {
        'grid-cols-[min-content_1fr]': iconPosition === 'start' && !hideLabel,
        'grid-cols-[min-content_0fr]': iconPosition === 'start' && hideLabel,
        'grid-cols-[1fr_min-content]': iconPosition === 'end' && !hideLabel,
        'grid-cols-[0fr_min-content]': iconPosition === 'end' && hideLabel,
    })

    const renderContents = (active?: boolean) => {
        const wrapperClassname = classnames(className, { active })

        return (
            <div className="relative">
                <div className={wrapperClassname}>
                    <div className={gridClassname}>
                        {iconPosition === 'start' && renderIcon()}
                        <div className="overflow-hidden">
                            <div className={labelClassname}>{label}</div>
                        </div>
                        {iconPosition === 'end' && renderIcon()}
                    </div>
                </div>
                {badge && !hideLabel && (
                    <div className="absolute bottom-0 left-0 top-0 w-[min-content] whitespace-nowrap">
                        <div className={wrapperClassname}>
                            <div className={gridClassname}>
                                {iconPosition === 'start' && renderIcon()}
                                <div className="flex items-center gap-2">
                                    <div className={labelClassname}>
                                        {label}
                                    </div>
                                    <Badge>{badge}</Badge>
                                </div>
                                {iconPosition === 'end' && renderIcon()}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        )
    }

    return renderFn({ item, renderContents })
}
